import { useEffect, useRef } from 'react';
import cx from 'classnames';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { resolveBoxSectionAnchorId } from '@fff-web/constants';
import {
  campaignState,
  getBoxOpenStatus,
  getSeasonalBoxCustomizeOpenStatus,
} from '../../../utils/campaignState';
import env from '../../../utils/env';
import { getVariantLabel } from '../../../utils/productDisplay';
import { sanityImage, sanityImageSrcSet } from '../../../utils/sanityImage';
import OrderSummary from '../SharedComponents/OrderSummary/OrderSummary';
import ReactivateCTA from '../SharedComponents/ReactivateCTA/ReactivateCTA';
import ActionCta from './components/ActionCta/ActionCta';
import * as styles from './BoxSubscriptionPanel.module.css';

/** `.productImage` renders at 100x100 (BoxSubscriptionPanel.module.css). */
const PRODUCT_IMAGE_WIDTH = 100;

const BEAUTY_BOX_OPEN_STATUS_COPY =
  'You have until the end of the month to customize your box. Don’t worry if you forget to customize. We’ll surprise you!';
const BEAUTY_BOX_CLOSED_STATUS_COPY =
  'Thanks for submitting your choices! We’re hard at work processing your box now.';
const BOX_TRACKING_AVAILABILITY_COPY = 'You can view tracking here as soon as it’s available.';
// Reminds recurring beauty-edit subscribers that paid selections won't
// auto-process with their box and require completing INSTANT checkout
// separately. Mirrors the alert on maui's customize summary page.
//
// NOTE: this copy may be removed or updated as part of future customize
// overhaul work — the recurring beauty-edit flow is expected to converge on a
// unified checkout path where the "additionals don't auto-process" caveat may
// no longer apply.
const BEAUTY_BOX_ADDITIONAL_CHOICES_COPY = 'Additional choices won’t auto-process with your box.';
const BEAUTY_BOX_ADDITIONAL_CHOICES_CTA_COPY = 'Checkout now to receive your extras.';

// A "swap for credit" selection trades a box choice for FabFitFun shopping
// credit. Growth reports a non-zero `unitPrice` for it, but that value is the
// credit the member RECEIVES — not a surcharge — so it must never reach the
// paid-choice math below. The flag lives on the variant (`SaleRawProduct` has
// no product-level `customizeInfo`), and a choice may reference either the
// product's own uuid or one of its variants', so index both.
const getSwapForCreditProductUuids = (products) => {
  const uuids = new Set();

  products.forEach((product) => {
    const creditVariants = (product.variants ?? []).filter(
      (variant) => variant.customizeInfo?.swapForCredit === true,
    );
    if (creditVariants.length === 0) return;

    if (product.productUuid) uuids.add(product.productUuid);
    creditVariants.forEach((variant) => uuids.add(variant.productUuid));
  });

  return uuids;
};

const getMaxChoiceGroups = (customizeSaleInfo) => {
  if (!customizeSaleInfo?.catalogUuidToCatalogMetadata) return 1;
  const choiceNumbers = Object.values(customizeSaleInfo.catalogUuidToCatalogMetadata)
    .filter(({ catalogChoiceType }) => catalogChoiceType === 'CHOICE')
    .map(({ choiceNumber }) => choiceNumber);
  return choiceNumbers.length > 0 ? Math.max(...choiceNumbers) : 1;
};

const BoxSubscriptionPanel = ({
  boxSubscription,
  sysdata,
  products = [],
  customizeSaleInfo,
  userExistingCart,
}) => {
  const anchorId = resolveBoxSectionAnchorId(boxSubscription.customizeSaleType);
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const hasSettledRef = useRef(false);

  const handleEditClick = (choiceNumber) => {
    window.location = `${env.envURL}/customize-box/${boxSlug}?q=${choiceNumber}`;
  };

  const handleManageSubscriptionClick = () => {
    window.location = `${env.envURL}/my-account`;
  };

  const renderProductItem = (product) => {
    const variantLabel = getVariantLabel(product?.displayVariant);
    const imageLabel = [product?.brandName, product?.name, variantLabel]
      .filter(Boolean)
      .join(' - ');

    return (
      <div key={product?.id} className={styles.productItem}>
        <img
          alt={imageLabel}
          className={styles.productImage}
          src={
            !product?.choiceProductUuid
              ? null
              : sanityImage(product?.displayVariant?.imageUrl, PRODUCT_IMAGE_WIDTH)
          }
          srcSet={
            !product?.choiceProductUuid
              ? undefined
              : sanityImageSrcSet(product?.displayVariant?.imageUrl, PRODUCT_IMAGE_WIDTH)
          }
        />
        <div className={styles.productDetails}>
          <h4 className={styles.productName}>Customization {product.choiceNumber}</h4>
          <span className={styles.productDescription}>
            {product?.brandName && (
              <span className={styles.productBrand}>{product.brandName} </span>
            )}
            {product?.name}
            {variantLabel && ` - ${variantLabel}`}
          </span>
          {(isSeasonalBox || (isBeautyBox && hasAdditionalPricing)) && product?.choiceType && (
            <span className={styles.productMeta}>
              {product.choiceQuantity} x{' '}
              {product.choiceType.charAt(0).toUpperCase() +
                product.choiceType.slice(1).toLowerCase()}{' '}
              Choice
              {product.choiceUnitPrice > 0 && ` - $${product.choiceUnitPrice}`}
            </span>
          )}
          {isOpen && (
            <button
              type="button"
              className={styles.editLink}
              onClick={() => {
                handleEditClick(product.choiceNumber);
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  //TODO: Analyze status handling, we decided to omit it for now as per discussed in
  // https://fabfitfun.slack.com/archives/C09AXQRGTDL/p1763579800116639

  const isSeasonalBox = boxSubscription.customizeSaleType === 'seasonal_customize_box';
  // For a beauty edit subscription, payment for paid selections is delegated to
  // maui's instant-checkout flow — the plutus panel swaps the CTA to "Checkout
  // Now" (handled inside ActionCta) so the user completes payment there instead.
  const isBeautyBox = boxSubscription.customizeSaleType === 'beauty_box';
  const status = isSeasonalBox
    ? getSeasonalBoxCustomizeOpenStatus(boxSubscription, sysdata.boxData)
    : getBoxOpenStatus(boxSubscription);
  const isOpen = status === campaignState.OPEN;
  const displayProducts = products.length > 0 ? products : [];
  const swapForCreditProductUuids = getSwapForCreditProductUuids(displayProducts);
  const choices = boxSubscription.choices ?? [];

  const hasProducts = displayProducts.length > 0 && choices.length > 0;

  useEffect(() => {
    if (!anchorId || hasSettledRef.current) return;
    if (window.location.hash.slice(1) !== anchorId) return;

    panelRef.current?.scrollIntoView({ block: 'start' });
    titleRef.current?.focus({ preventScroll: true });
    hasSettledRef.current = hasProducts;
  }, [anchorId, hasProducts]);

  const boxSlug =
    boxSubscription?.brandName?.toLowerCase().replace(/\s+/g, '-') +
    '-' +
    boxSubscription?.name?.toLowerCase().replace(/\s+/g, '-');

  const generateChoiceGroups = (choices = []) => {
    const TOTAL_CHOICE_GROUPS = getMaxChoiceGroups(customizeSaleInfo);
    const groups = Array.from({ length: TOTAL_CHOICE_GROUPS }, (_, i) => ({
      choiceNumber: i + 1,
      items: [],
    }));

    choices.forEach((choice) => {
      if (choice.choiceNumber >= 1 && choice.choiceNumber <= TOTAL_CHOICE_GROUPS) {
        groups[choice.choiceNumber - 1].items.push(choice);
      }
    });

    groups.forEach((group) => {
      group.items.sort((a, b) => {
        if (a.choiceType === 'PRIMARY' && b.choiceType !== 'PRIMARY') return -1;
        if (a.choiceType !== 'PRIMARY' && b.choiceType === 'PRIMARY') return 1;
        return (a.unitPrice ?? 0) - (b.unitPrice ?? 0);
      });
    });

    return groups;
  };

  const choiceGroups = generateChoiceGroups(choices);

  const sortedProducts = choiceGroups.flatMap((group) => {
    if (group.items.length === 0) {
      return [
        {
          choiceNumber: group.choiceNumber,
          choiceProductUuid: null,
        },
      ];
    }

    return group.items.map((choice) => {
      const matchedProduct = displayProducts.find((product) => {
        const productMatch = product.productUuid === choice.productUuid;
        const variantMatch = product.variants?.some(
          (variant) => variant.productUuid === choice.productUuid,
        );
        return productMatch || variantMatch;
      });

      const matchingVariant = matchedProduct?.variants?.find(
        (variant) => variant.productUuid === choice.productUuid,
      );

      const displayVariant =
        matchingVariant ??
        (matchedProduct?.variants?.length === 1 ? matchedProduct.variants[0] : undefined);

      const isSwapForCredit = swapForCreditProductUuids.has(choice.productUuid);

      return {
        ...(matchedProduct ?? {}),
        choiceProductUuid: choice.productUuid,
        choiceNumber: choice.choiceNumber,
        catalogUuid: matchingVariant?.catalogUuid,
        displayVariant,
        choiceType: choice.choiceType,
        choiceQuantity: choice.quantity,
        // Swap-for-credit selections carry the credit amount as their
        // `unitPrice`. Zeroing it here is the single choke point that keeps
        // them out of every downstream `choiceUnitPrice > 0` paid check —
        // the order summary, the price badge, and ActionCta's branching.
        choiceUnitPrice: isSwapForCredit ? 0 : choice.unitPrice,
      };
    });
  });

  const paidChoices = sortedProducts.filter((p) => p.choiceUnitPrice > 0);
  const orderSummaryTotal = paidChoices.reduce((acc, p) => acc + (p.choiceQuantity ?? 1), 0);
  const orderSummarySum = paidChoices.reduce(
    (acc, p) => acc + p.choiceUnitPrice * (p.choiceQuantity ?? 1),
    0,
  );
  const hasAdditionalPricing = orderSummaryTotal > 0;

  const hasPartialSelections = sortedProducts.some((product) => product.choiceProductUuid === null);
  const isCustomizationClosed = !isSeasonalBox && status !== campaignState.OPEN;
  const boxSubscriptionsCopy = !isSeasonalBox
    ? status === campaignState.OPEN
      ? BEAUTY_BOX_OPEN_STATUS_COPY
      : BEAUTY_BOX_CLOSED_STATUS_COPY
    : undefined;

  return (
    <>
      <div className={styles.panelContainer} id={anchorId} ref={panelRef}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title} ref={titleRef} tabIndex={-1}>
            {boxSubscription.name}
          </h1>
          <div className={styles.manageSubscriptionContainer}>
            <button
              type="button"
              className={styles.manageSubscription}
              onClick={handleManageSubscriptionClick}
            >
              Manage Subscription
            </button>
          </div>
        </div>

        {/* Customizations Section */}
        <div className={styles.customizationsSection}>
          <h2 className={styles.customizationsHeader}>Customizations</h2>
          {boxSubscriptionsCopy && (
            <p className={styles.description}>
              {boxSubscriptionsCopy}
              {isCustomizationClosed && (
                <span className={styles.descriptionLine}>{BOX_TRACKING_AVAILABILITY_COPY}</span>
              )}
            </p>
          )}
          {isBeautyBox && isOpen && hasAdditionalPricing && (
            <p className={styles.description}>
              {BEAUTY_BOX_ADDITIONAL_CHOICES_COPY}{' '}
              <strong>{BEAUTY_BOX_ADDITIONAL_CHOICES_CTA_COPY}</strong>
            </p>
          )}
          <div className={styles.statusContainer}>
            <div className={styles.status}>
              Status: {status || 'Open'}
              <div
                className={cx(styles.statusLight, {
                  [styles.open]: isOpen,
                })}
              />
            </div>
          </div>
        </div>

        {/* Main Container - matches Panel structure */}
        <div className={cx(styles.container, { [styles.centered]: !hasProducts })}>
          <div className={styles.productGridWrapper}>
            {!hasProducts ? (
              <ReactivateCTA
                baseClass="customize"
                title="Customizations are Open!"
                ctaLabel="Customize Now"
                iconClass="fa-hand-holding-box"
                isMobile={sysdata.isMobile}
                isCustomizationFlow
                boxSlug={boxSlug}
              />
            ) : (
              <div className={styles.productGrid}>{sortedProducts.map(renderProductItem)}</div>
            )}
          </div>
          {hasProducts && (
            <div className={styles.summaryDetails}>
              {hasAdditionalPricing && (
                <OrderSummary
                  title="Extra Choices"
                  total={orderSummaryTotal}
                  sum={orderSummarySum}
                  country={sysdata.userData?.shippingCountry ?? ''}
                  footnote="Total does not reflect credit applied."
                />
              )}
              <ActionCta
                hasPartialSelections={hasPartialSelections}
                hasAdditionalPricing={hasAdditionalPricing}
                boxSlug={boxSlug}
                isOpen={isOpen}
                isProcessBoxEnabled={!isSeasonalBox}
                userExistingCart={userExistingCart}
                boxSubscription={boxSubscription}
                sortedProducts={sortedProducts}
                isBeautyBox={isBeautyBox}
              />
            </div>
          )}
        </div>
      </div>
      <div className="hor-rule mar-h-40" />
    </>
  );
};

BoxSubscriptionPanel.propTypes = {
  boxSubscription: shape({}).isRequired,
  customizeSaleInfo: object,
  sysdata: shape({
    boxData: shape({
      customizeCampaignState: shape({}).isRequired,
      customizations: arrayOf(
        shape({
          additionalItems: arrayOf(
            shape({
              quantity: number.isRequired,
              price: number.isRequired,
            }),
          ).isRequired,
          questionType: string.isRequired,
          additionalItemCost: number.isRequired,
        }),
      ).isRequired,
    }).isRequired,
    userData: shape({}).isRequired,
    isMobile: bool.isRequired,
    revealCallback: func.isRequired,
  }).isRequired,
  products: arrayOf(shape({})),
  userExistingCart: shape({}),
};

BoxSubscriptionPanel.displayName = 'BoxSubscriptionPanel';

export default BoxSubscriptionPanel;
